/**
 * Local, read-only mirror of the Lens DSL shape (view-runtime §"lens/v1").
 *
 * The canonical Zod schema (`lensDslSchema`, `ViewPacketV3`, `validateLensDsl`)
 * lives in `packages/shared/src/schemas/view-runtime.ts` but is intentionally
 * NOT re-exported from the `@aos/shared` barrel (`schemas/index.ts` exports only
 * entities / runtime / agent-output). The portal therefore cannot import the DSL
 * type from `@aos/shared`; this file is the local projection interface the Lens
 * Builder renders. It is a structural subset — every field is optional/defensive
 * because the value arriving from the worker is a stored `dsl_json` string that
 * the builder only ever READS (projection doctrine — never mutates business
 * truth; authoring/validation calls go to the server, which enforces).
 */

/** A `{ primitive, where }` clause from `query.include` / `query.exclude`. */
export interface LensQueryClause {
  primitive: string;
  where?: Record<string, unknown>;
}

/** A `{ region|slot, component, source }` binding (components / panels). */
export interface LensComponentBinding {
  region?: string;
  slot?: string;
  component: string;
  source: string;
}

/** An `{ id, capability }` action-intent reference into the registry. */
export interface LensActionIntent {
  id: string;
  capability: string;
}

/** A `{ concept, shape }` visual-encoding rule. */
export interface LensVisualEncoding {
  concept: string;
  shape: string;
}

/** The parsed `lens/v1` DSL document, as projected for read-only display. */
export interface LensDslDocument {
  dsl_version?: string;
  metadata?: { name?: string; description?: string };
  root_types?: string[];
  query?: {
    include?: LensQueryClause[];
    exclude?: LensQueryClause[];
  };
  semantic_levels?: string[];
  components?: LensComponentBinding[];
  panels?: LensComponentBinding[];
  action_intents?: LensActionIntent[];
  visual_encodings?: LensVisualEncoding[];
  layout?: { default_engine?: string };
  limits?: { max_nodes?: number; max_edges?: number };
  redaction_display_policy?: string;
}

/** Result of parsing a stored `dsl_json` string for display. */
export type LensDslParse =
  | { ok: true; doc: LensDslDocument; raw: string }
  | { ok: false; error: string; raw: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Safely parse a `Lens.dsl_json` string into the display projection. Never
 * throws — a malformed document degrades to `{ ok: false }` so the screen can
 * route it through the RenderState `error` state instead of crashing.
 */
export function parseLensDsl(dslJson: string | null | undefined): LensDslParse {
  const raw = dslJson ?? "";
  if (raw.trim().length === 0) {
    return { ok: false, error: "Lens has no DSL document.", raw };
  }
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch (cause) {
    return {
      ok: false,
      error: cause instanceof Error ? cause.message : "Invalid DSL JSON.",
      raw,
    };
  }
  if (!isRecord(value)) {
    return { ok: false, error: "DSL document is not an object.", raw };
  }
  return { ok: true, doc: value as LensDslDocument, raw };
}

/**
 * Parse a `root_types_json` column (a JSON string array) into a `string[]`,
 * tolerating an absent/garbled value (returns `[]`).
 */
export function parseRootTypes(rootTypesJson: string | null | undefined): string[] {
  if (!rootTypesJson) return [];
  try {
    const value = JSON.parse(rootTypesJson) as unknown;
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === "string");
    }
  } catch {
    /* fall through to [] */
  }
  return [];
}

/** Pretty-print any JSON-ish value for the read-only spec preview. */
export function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
